import axios from 'axios';
import { toast, toastError } from '../utils/toast';
import { clearAllAuthStorage, isAuthSuperseded } from '../utils/authSession';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
api.interceptors.request.use(
  (config) => {
    if (isAuthSuperseded()) {
      return config;
    }
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle response errors (401 = clear token; 429 = rate limit, do not clear token)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const url = String(error.config?.url || '');
    const isAuthLoginRoute = url.includes('/auth/login');
    const isLoginPage = typeof window !== 'undefined' && window.location.pathname === '/login';
    const hasAuthToken = typeof localStorage !== 'undefined' && Boolean(localStorage.getItem('token'));
    const hadAuthHeader = Boolean(error.config?.headers?.Authorization);
    const isGuest = typeof localStorage !== 'undefined' && Boolean(localStorage.getItem('guestSessionExpiry'));

    if (status === 401 && !isGuest && (hadAuthHeader || hasAuthToken) && !isAuthLoginRoute && !isLoginPage) {
      const msg = String(error.response?.data?.error || '');
      if (msg.toLowerCase().includes('session expired due to another login')) {
        // Avoid duplicate toasts during redirect loops.
        try {
          const key = 'tv_toast_session_superseded';
          if (!sessionStorage.getItem(key)) {
            sessionStorage.setItem(key, '1');
            toastError('You were logged out because this account signed in on another browser/tab.');
          }
        } catch (_) {}
      }
      clearAllAuthStorage();
      window.location.href = '/login';
    }
    // 429: rate limit – backend does not allow exceeding timing; pass through for UI to show message
    if (status !== 401 && status !== 429) {
      toastError(error, 'Request failed');
    }
    return Promise.reject(error);
  }
);

// Generic CRUD operations
export const createCrudApi = (endpoint) => ({
  getAll: (params = {}) => api.get(`/${endpoint}`, { params }).then(res => res.data),
  getById: (id) => api.get(`/${endpoint}/${id}`).then(res => res.data),
  create: (data) => api.post(`/${endpoint}`, data).then(res => res.data),
  update: (id, data) => api.put(`/${endpoint}/${id}`, data).then(res => res.data),
  delete: (id) => api.delete(`/${endpoint}/${id}`).then(res => res.data),
});

// Specific API endpoints
export const tilesApi = createCrudApi('tiles');
export const kitchensApi = createCrudApi('kitchens');
export const bathroomsApi = createCrudApi('bathrooms');
export const bedroomsApi = createCrudApi('bed-rooms');
export const livingRoomsApi = createCrudApi('living-rooms');
export const commercialApi = createCrudApi('commercial');
export const elevationsApi = createCrudApi('elevations');
export const tabletopsApi = createCrudApi('tabletops');
export const stepRisersApi = createCrudApi('step-risers');

// Auth API (token-based; backend rate-limited and JWT expiry enforced)
export const authApi = {
  login: (credentials) => api.post('/auth/login', credentials).then(res => res.data),
  verify2FALogin: (data) => api.post('/auth/login/2fa', data).then(res => res.data),
  resend2FAEmailOtp: (data) => api.post('/auth/login/2fa/resend', data).then(res => res.data),
  get2FAStatus: () => api.get('/auth/2fa/status').then(res => res.data),
  setup2FA: (method) => api.post('/auth/2fa/setup', { method }).then(res => res.data),
  verify2FASetup: (code) => api.post('/auth/2fa/verify-setup', { code }).then(res => res.data),
  disable2FA: (password) => api.post('/auth/2fa/disable', { password }).then(res => res.data),
  regenerateBackupCodes: (password) => api.post('/auth/2fa/backup-codes', { password }).then(res => res.data),
  resetUser2FA: (id) => api.post(`/auth/users/${id}/reset-2fa`).then(res => res.data),
  getProfile: () => api.get('/auth/profile').then(res => res.data),
  updateProfile: (data) => api.put('/auth/profile', data).then(res => res.data),
  logout: () => {
    clearAllAuthStorage();
    return Promise.resolve();
  },
  // Super Admin & Org Admin user management
  createUser: (data) => api.post('/auth/users', data).then(res => res.data),
  listUsers: (params = {}) => api.get('/auth/users', { params }).then(res => res.data),
  updateUser: (id, data) => api.patch(`/auth/users/${id}`, data).then(res => res.data),
  updateUserStatus: (id, isActive) => api.patch(`/auth/users/${id}/status`, { isActive }).then(res => res.data),
  deleteUser: (id) => api.delete(`/auth/users/${id}`).then(res => res.data),
  resetUserPassword: (id) => api.post(`/auth/users/${id}/reset-password`).then(res => res.data),
};

// Organization Management APIs
export const organizationApi = {
  getAll: (params = {}) =>
    api.get('/organizations', { params })
      .catch((err) => {
        if (err.response?.status === 404) return api.get('/api/organizations', { params });
        throw err;
      })
      .then((res) => res.data),

  getById: (id) =>
    api.get(`/organizations/${id}`)
      .catch((err) => {
        if (err.response?.status === 404) return api.get(`/api/organizations/${id}`);
        throw err;
      })
      .then((res) => res.data),

  create: (data) =>
    api.post('/organizations', data)
      .catch((err) => {
        if (err.response?.status === 404) return api.post('/api/organizations', data);
        throw err;
      })
      .then((res) => res.data),

  update: (id, data) =>
    api.put(`/organizations/${id}`, data)
      .catch((err) => {
        if (err.response?.status === 404) return api.put(`/api/organizations/${id}`, data);
        throw err;
      })
      .then((res) => res.data),

  updateStatus: (id, isActive) =>
    api.patch(`/organizations/${id}/status`, { isActive })
      .catch((err) => {
        if (err.response?.status === 404) return api.patch(`/api/organizations/${id}/status`, { isActive });
        throw err;
      })
      .then((res) => res.data),

  delete: (id) =>
    api.delete(`/organizations/${id}`)
      .catch((err) => {
        if (err.response?.status === 404) return api.delete(`/api/organizations/${id}`);
        throw err;
      })
      .then((res) => res.data),

  // Organization-scoped users (with userLimit enforcement)
  getUsers: (orgId) =>
    api.get(`/organizations/${orgId}/users`)
      .catch((err) => {
        if (err.response?.status === 404) return api.get('/auth/users', { params: { organizationId: orgId } });
        throw err;
      })
      .then((res) => res.data),

  addUser: (orgId, data) =>
    api.post(`/organizations/${orgId}/users`, data)
      .catch((err) => {
        if (err.response?.status === 404) return api.post('/auth/users', { ...data, organizationId: orgId });
        throw err;
      })
      .then((res) => res.data),

  updateUserStatus: (orgId, userId, isActive) =>
    api.patch(`/organizations/${orgId}/users/${userId}/status`, { isActive })
      .catch((err) => {
        if (err.response?.status === 404) return api.patch(`/auth/users/${userId}/status`, { isActive });
        throw err;
      })
      .then((res) => res.data),

  deleteUser: (orgId, userId) =>
    api.delete(`/organizations/${orgId}/users/${userId}`)
      .catch((err) => {
        if (err.response?.status === 404) return api.delete(`/auth/users/${userId}`);
        throw err;
      })
      .then((res) => res.data),

  // Subscription & Renewal
  getSubscription: (orgId) =>
    api.get(`/organizations/${orgId}/subscription`)
      .catch((err) => {
        if (err.response?.status === 404) return api.get(`/subscriptions/organization/${orgId}`);
        throw err;
      })
      .then((res) => res.data),

  renewSubscription: (orgId, data) =>
    api.post(`/organizations/${orgId}/subscription/renew`, data)
      .catch((err) => {
        if (err.response?.status === 404) return api.post('/auth/renew', { ...data, organizationId: orgId });
        throw err;
      })
      .then((res) => res.data),
};

// Subscription APIs
export const subscriptionApi = {
  getAll: () => api.get('/subscriptions').then((res) => res.data),
  getByOrgId: (orgId) => organizationApi.getSubscription(orgId),
  renew: (orgId, data) => organizationApi.renewSubscription(orgId, data),
};

// Contact form (rate-limited on backend; no token for submit; list requires auth)
export const contactApi = {
  submit: (data) => api.post('/contact', data).then(res => res.data),
  list: (params = {}) => api.get('/contact/submissions', { params }).then(res => res.data),
};

// Room images: list and upload to AWS S3 (no local storage). List returns [{ filename, key, url, canDelete?, uploadedBy? }].
// Backend should return only current user's rooms + super admin default rooms when using token; canDelete/uploadedBy for delete visibility.
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
export const getRoomImageList = () => api.get('/api/rooms/images').then(res => res.data);
export const getRoomImageListByCategory = (category) =>
  api.get('/api/rooms/images', { params: { category } }).then(res => res.data);
export const getRoomCategories = () => api.get('/api/rooms/categories').then(res => res.data);
/** Use item.url from list/upload response when available; fallback for legacy filename-only. */
export const getRoomImageUrl = (filenameOrItem) => {
  if (typeof filenameOrItem === 'object' && filenameOrItem?.url) return filenameOrItem.url;
  return `${API_BASE}/app/images/${filenameOrItem}`;
};
export const uploadRoomImage = (formData) =>
  api.post('/api/rooms/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }).then(res => res.data);
/** Delete a room image by key (from list response). Body: { key } or query ?key= */
export const deleteRoomImage = (key) =>
  api.delete('/api/rooms/images', { data: { key } }).then(res => res.data);
export const deleteRoomImageById = (id) =>
  api.delete(`/api/rooms/images/${id}`).then(res => res.data);
/** Save design (canvas) image to cloud. Body: { image: "data:image/png;base64,..." }. Returns { url, key }. */
export const exportRoomDesign = (body) => api.post('/api/rooms/export', body).then(res => res.data);

// Visualizer tiles: backend may return default + user-scoped tiles. Super-admin upload (multipart):
//   visibilityMode: 'default' | 'particular'
//   assignedUserIds: JSON string array of admin user ids, e.g. '["id1","id2"]' — required when particular.
// Persist on tile doc as visibilityMode + assignedUserIds[]; filter GET /api/rooms/tiles by JWT user.
export const getVisualizerTiles = (params = {}) =>
  api.get('/api/rooms/tiles', { params }).then((res) => res.data);
export const getVisualizerTile = (id) =>
  api.get(`/api/rooms/tiles/${id}`).then((res) => res.data);
export const uploadVisualizerTile = (formData) =>
  api.post('/api/rooms/tiles/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }).then((res) => res.data);
export const updateVisualizerTile = (id, data) =>
  api.patch(`/api/rooms/tiles/${id}`, data).then((res) => res.data);
export const deleteVisualizerTile = (id) =>
  api.delete(`/api/rooms/tiles/${id}`).then((res) => res.data);

// App settings (image cloud storage; tilePickerMode: 'dropdown' | 'fullList' for design sidepanel). Super admin can update.
export const getAppSettings = () => api.get('/api/app-settings').then(res => res.data);
export const updateAppSettings = (data) => api.patch('/api/app-settings', data).then(res => res.data);

// Tile specific operations
export const tileOperationsApi = {
  upload: (formData) => api.post('/tiles/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }).then(res => res.data),
  createScene: (formData) => api.post('/tiles/scenes', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }).then(res => res.data),
  applyTile: (data) => api.post('/tiles/apply', data).then(res => res.data),
};

export default api;
